import { COMPOUND_LIB, FOUNDATION_COMPOUNDS } from "@/services/mock";
import type { FormulationEntry } from "@/utils/types/compound";
import type { CostBreakdown } from "@/utils/formulation/computeCost";
import { lookupServerLens } from "@/utils/serverCompounds";
import { LossTile } from "./LossTile";
import { CostTile } from "./CostTile";

const ALL_COMPOUNDS = [...COMPOUND_LIB, ...FOUNDATION_COMPOUNDS];
const compoundById = (id: string) => ALL_COMPOUNDS.find((c) => c.id === id);

// Color priority: server-resolved family color (matches FlaskRow's stripe)
// → mock library family CSS var (for DevShowcase fixtures) → neutral.
function familyColorFor(id: string): string {
  const serverColor = lookupServerLens(id)?.family?.color;
  if (serverColor) return serverColor;
  const mockFamily = compoundById(id)?.family;
  return `var(--fam-${mockFamily || "neutral"})`;
}

type CostUnit = "10g" | "100g" | "1kg" | "batch";

type FlaskBarProps = {
  formulation: FormulationEntry[];
  foundation: FormulationEntry[];
  // KPI tiles — when supplied, the bar renders LossTile + CostTile on its
  // left, separated from the flavor/foundation labels by a vertical divider.
  // Matches the design: KPIs live in the body strip, not the header.
  loss?: number | null;
  lossPrev?: number | null;
  hasTarget?: boolean;
  cost?: CostBreakdown | null;
  costUnit?: CostUnit;
  setCostUnit?: (u: CostUnit) => void;
  useMedianFallback?: boolean;
  setUseMedianFallback?: (next: boolean) => void;
  medianPricePerGramUsd?: number | null;
  resolveName?: (compoundId: string) => string;
};

export function FlaskBar({
  formulation,
  foundation,
  loss = null,
  lossPrev = null,
  hasTarget = false,
  cost = null,
  costUnit,
  setCostUnit,
  useMedianFallback,
  setUseMedianFallback,
  medianPricePerGramUsd = null,
  resolveName,
}: FlaskBarProps) {
  const flavorTotal = formulation.reduce((s, f) => s + f.ppm, 0);
  const foundTotal = foundation.reduce((s, f) => s + f.ppm, 0);
  const grandTotal = flavorTotal + foundTotal;

  const flavorPct = grandTotal ? (flavorTotal / grandTotal) * 100 : 100;
  const foundPctV = 100 - flavorPct;
  const showFound = foundPctV > 0;

  const flavorW = grandTotal ? (flavorTotal / grandTotal) * 400 : 400;
  const foundW = 400 - flavorW;

  // KPIs render only when both loss + cost wires are connected — guards
  // against half-connected callers (e.g. the dev showcase).
  const showKpi =
    costUnit !== undefined &&
    setCostUnit !== undefined &&
    useMedianFallback !== undefined &&
    setUseMedianFallback !== undefined &&
    resolveName !== undefined;

  const labelsAndBar = (
    <div style={{ flex: 1, minWidth: 0 }}>
      <div className="flask-bar-labels">
        {/* Flavor label — pinned to the left edge. Width is auto so the
            value+ppm+% inline group can use its natural length and never
            wraps, even when the flavor segment is a tiny slice of the bar. */}
        <div
          className="vstack"
          style={{ gap: 1, lineHeight: 1, alignItems: "flex-start" }}
        >
          <span
            className="mono"
            style={{
              fontSize: 9,
              color: "var(--ink-4)",
              letterSpacing: "0.1em",
              textTransform: "uppercase",
            }}
          >
            Flavor
          </span>
          <span
            className="hstack"
            style={{
              alignItems: "baseline",
              gap: 5,
              whiteSpace: "nowrap",
              flexWrap: "nowrap",
            }}
          >
            <span
              style={{
                fontSize: 13,
                color: "var(--ink)",
                fontWeight: 500,
                whiteSpace: "nowrap",
              }}
            >
              {flavorTotal.toFixed(1)}
              <span
                style={{ fontSize: 10, color: "var(--ink-4)", fontWeight: 400 }}
              >
                {" "}
                ppm
              </span>
            </span>
            <span className="mono" style={{ fontSize: 10, color: "var(--ink-3)" }}>
              · {flavorPct.toFixed(1)}%
            </span>
          </span>
        </div>

        {showFound && (
          <div
            className="vstack"
            style={{ gap: 1, lineHeight: 1, alignItems: "flex-end" }}
          >
            <span
              className="mono"
              style={{
                fontSize: 9,
                color: "var(--ink-4)",
                letterSpacing: "0.1em",
                textTransform: "uppercase",
              }}
            >
              Foundation
            </span>
            <span
              className="hstack"
              style={{
                alignItems: "baseline",
                gap: 5,
                whiteSpace: "nowrap",
                flexWrap: "nowrap",
              }}
            >
              <span
                style={{
                  fontSize: 13,
                  color: "var(--ink-2)",
                  fontWeight: 500,
                  whiteSpace: "nowrap",
                }}
              >
                {foundTotal.toFixed(1)}
                <span
                  style={{ fontSize: 10, color: "var(--ink-4)", fontWeight: 400 }}
                >
                  {" "}
                  ppm
                </span>
              </span>
              <span className="mono" style={{ fontSize: 10, color: "var(--ink-3)" }}>
                · {foundPctV.toFixed(1)}%
              </span>
            </span>
          </div>
        )}
      </div>

      <svg
        viewBox="0 0 400 16"
        preserveAspectRatio="none"
        style={{ width: "100%", height: 16, display: "block" }}
      >
        <g>
          {(() => {
            let x = 0;
            return formulation.map((f) => {
              const w = grandTotal ? (f.ppm / grandTotal) * 400 : 0;
              const rect = (
                <rect
                  key={f.compoundId}
                  x={x}
                  y={0}
                  width={Math.max(w, 0.5)}
                  height={16}
                  fill={familyColorFor(f.compoundId)}
                  fillOpacity="0.85"
                />
              );
              x += w;
              return rect;
            });
          })()}
          {foundW > 0 && (
            <rect
              x={flavorW}
              y={0}
              width={foundW}
              height={16}
              fill="var(--fam-neutral)"
              fillOpacity="0.55"
              style={{
                stroke: "var(--line-strong)",
                strokeDasharray: "2 2",
                strokeWidth: 0.5,
              }}
            />
          )}
          <rect
            x="0.3"
            y="0.3"
            width="399.4"
            height="15.4"
            fill="none"
            stroke="var(--line-strong)"
            strokeWidth="0.6"
          />
          {foundW > 8 && (
            <line
              x1={flavorW}
              y1="0"
              x2={flavorW}
              y2="16"
              stroke="var(--ink)"
              strokeWidth="0.8"
            />
          )}
        </g>
      </svg>
    </div>
  );

  if (!showKpi) {
    return <div className="flask-bar-wrap">{labelsAndBar}</div>;
  }

  return (
    <div
      className="flask-bar-wrap hstack"
      style={{ gap: 24, alignItems: "stretch" }}
    >
      <div
        className="hstack"
        style={{ alignItems: "center", flexShrink: 0, gap: 18 }}
      >
        <LossTile loss={loss} prev={lossPrev} hasTarget={hasTarget} />
        <CostTile
          cost={cost}
          unit={costUnit!}
          setUnit={setCostUnit!}
          useMedianFallback={useMedianFallback!}
          setUseMedianFallback={setUseMedianFallback!}
          medianPricePerGramUsd={medianPricePerGramUsd}
          resolveName={resolveName!}
        />
      </div>
      <span
        style={{ width: 1, alignSelf: "stretch", background: "var(--line)" }}
      />
      {labelsAndBar}
    </div>
  );
}
